import { tableHelper, tableFormulas } from '@builttocreate/engine-utils';

import FieldTableColumnTypes from '../constants/FieldTableColumnTypes';

/**
 * <--------------NOTICE----------------->
 * Row helpers for large table and collection fields (10k+ rows).
 *
 * These mirror helpers in @builttocreate/engine-utils and return the same results. The engine
 * versions are quadratic or rebuild a whole-table lookup on every call, which costs seconds per
 * render once a field holds thousands of rows. Keep these in sync with the engine if its
 * behaviour changes (tableRowsHelper.test.js compares them against the engine).
 */

const { getRows } = tableHelper;
const { getResolvedCellsByIndex: getEngineResolvedCellsByIndex, getColumnFormula, getDisplayCellValue, filterLookupColumns } = tableFormulas;

/**
 * Same result as tableHelper.getCleanedRowOrder: the rowOrder (or the undeleted row ids when it is
 * empty) without ids of missing / deleted rows and without duplicates (first occurrence wins).
 *
 * The engine dedupes with indexOf, which is O(n²) and takes ~800ms at 11k rows.
 *
 * @param {Array} rowOrder
 * @param {Array} rows
 * @returns {Array}
 */
export const getCleanedRowOrder = (rowOrder, rows) => {

  const safeRows = rows || [];

  let nextRowOrder = [];

  if (rowOrder && rowOrder.length > 0) {
    nextRowOrder = rowOrder;
  } else {
    safeRows.forEach((row) => {
      if (!row.deleted) nextRowOrder.push(row._id);
    });
  }

  //The last row with an id decides whether that id is active, as in the engine
  const activeLookup = new Map();
  safeRows.forEach((row) => activeLookup.set(row._id, !!row && !row.deleted));

  const seen = new Set();
  const cleanedRowOrder = [];

  nextRowOrder.forEach((rowId) => {
    if (!rowId || !activeLookup.get(rowId) || seen.has(rowId)) return;
    seen.add(rowId);
    cleanedRowOrder.push(rowId);
  });

  return cleanedRowOrder;

};

/**
 * Same result as tableFormulas.getOrderedRows: the rows the grid renders first, in its order,
 * then every other undeleted row appended once by array index.
 *
 * @param {Array} rows
 * @param {Array} rowOrder
 * @returns {Array}
 */
export const getOrderedRows = (rows, rowOrder) => {

  const activeRows = getRows(rows);
  const cleanedRowOrder = getCleanedRowOrder(rowOrder, activeRows);

  const lastIndexById = new Map();
  activeRows.forEach((row, index) => lastIndexById.set(row._id, index));

  const consumed = new Set();
  const ordered = [];

  cleanedRowOrder.forEach((rowId) => {
    const index = lastIndexById.get(rowId);
    if (index !== undefined && !consumed.has(index)) {
      consumed.add(index);
      ordered.push(activeRows[index]);
    }
  });

  activeRows.forEach((row, index) => {
    if (!consumed.has(index)) {
      consumed.add(index);
      ordered.push(row);
    }
  });

  return ordered;

};

/**
 * Marker cached for a row that holds a formula. Such a row depends on other rows, so the whole
 * table goes through the engine.
 */
const FORMULA_ROW = {};

/**
 * columns array -> { activeColumns, optionLookups, hasColumnFormula, rowCells: WeakMap<row, cells> }
 */
const resolvedCellsCache = new WeakMap();

const getColumnsEntry = (columns, cache) => {

  let entry = cache.get(columns);
  if (entry) return entry;

  const activeColumns = filterLookupColumns(columns);
  const optionLookups = {};

  activeColumns.forEach((column) => {
    if (column.type !== FieldTableColumnTypes.dropdown) return;
    optionLookups[column._id] = {};
    (column.options || []).forEach((option) => { optionLookups[column._id][option._id] = option.value; });
  });

  entry = {
    activeColumns,
    optionLookups,
    hasColumnFormula: activeColumns.some((column) => !!getColumnFormula(column)),
    rowCells: new WeakMap(),
  };

  cache.set(columns, entry);
  return entry;

};

/**
 * The display values of one row when nothing in the table is a formula. Each value then only
 * depends on its own cell, exactly as generateTableLookup + getDisplayCellValue compute it.
 */
const getRowCellsWithoutFormulas = (row, entry) => {

  const { activeColumns, optionLookups } = entry;
  const rowCells = row && row.cells;
  const cells = {};

  for (let i = 0; i < activeColumns.length; i++) {

    const column = activeColumns[i];
    const raw = rowCells ? rowCells[column._id] : undefined;

    //Any text that could be read as a formula (legacy A1 or row path) needs the engine
    if (typeof raw === 'string' && raw.trim().charAt(0) === '=') return FORMULA_ROW;

    let cellValue;
    if (raw && typeof raw === 'string') cellValue = raw.trim();
    if (raw && typeof raw === 'number') cellValue = raw.toString();

    let lookupValue;
    if (cellValue && column.type === FieldTableColumnTypes.dropdown) lookupValue = optionLookups[column._id][cellValue];
    else if ((cellValue || raw) && column.type === FieldTableColumnTypes.image) lookupValue = raw;
    else lookupValue = cellValue || '';

    cells[column._id] = getDisplayCellValue(raw, column, lookupValue);

  }

  return cells;

};

/**
 * Same result as tableFormulas.getResolvedCellsByIndex: display values for every undeleted row,
 * by position, keyed by column id.
 *
 * When no column formula and no formula-like cell exists, every row resolves on its own, so the
 * result is cached per row object (and per columns array). Rows that did not change keep their
 * cached cells, which makes an edit in an 11k row table cost one row instead of the whole table.
 * Anything formula related falls back to the engine. The returned cell objects are shared
 * between calls and must be treated as read-only.
 *
 * @param {Array} rows     rows in grid order (see getOrderedRows)
 * @param {Array} columns  columns in grid order
 * @param {WeakMap} cache  optional operation-scoped cache for mutable input; defaults to the render cache
 * @returns {Array}
 */
export const getResolvedCellsByIndex = (rows, columns, cache = resolvedCellsCache) => {

  if (!Array.isArray(rows) || !Array.isArray(columns)) return getEngineResolvedCellsByIndex(rows, columns);

  const entry = getColumnsEntry(columns, cache);

  if (entry.hasColumnFormula) return getEngineResolvedCellsByIndex(rows, columns);

  const resolved = [];

  for (let i = 0; i < rows.length; i++) {

    const row = rows[i];

    if (!row.deleted) {

      let cells = entry.rowCells.get(row);

      if (cells === undefined) {
        cells = getRowCellsWithoutFormulas(row, entry);
        entry.rowCells.set(row, cells);
      }

      if (cells === FORMULA_ROW) return getEngineResolvedCellsByIndex(rows, columns);

      resolved.push(cells);

    }

  }

  return resolved;

};
